import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Chip,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import { useNoteMutations } from '../../hooks/useNoteMutations';
import { useClients } from '../../hooks/useClients';
import type { NoteType } from '../../types/note';
import { NOTE_TYPE_OPTIONS } from '../../types/note';
import type { Client } from '../../services/clients';

interface QuickCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const QuickCaptureModal: React.FC<QuickCaptureModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<NoteType>('quick');
  const [clientId, setClientId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { createNote } = useNoteMutations();
  const { data: clientsData } = useClients();
  const clients: Client[] = clientsData?.clients || [];

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setTitle('');
      setContent('');
      setType('quick');
      setClientId('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    try {
      await createNote.mutateAsync({
        title: title.trim(),
        content,
        type,
        clientId: clientId || undefined,
      });
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to create note');
    }
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Cmd/Ctrl + Enter to save
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [title, content, type, clientId]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          borderTop: '4px solid',
          borderColor: 'primary.main',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <BoltIcon color="primary" />
            <Typography variant="h6">Quick Capture</Typography>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Title */}
          <TextField
            label="What's on your mind?"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setError(null);
            }}
            fullWidth
            autoFocus
            error={!!error}
            helperText={error}
            placeholder="Enter a title..."
          />

          {/* Content */}
          <TextField
            label="Details (optional)"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Add any notes or context..."
          />

          {/* Type selection */}
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Note Type
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
              {NOTE_TYPE_OPTIONS.map((option) => (
                <Chip
                  key={option.value}
                  label={`${option.icon} ${option.label}`}
                  onClick={() => setType(option.value)}
                  color={type === option.value ? 'primary' : 'default'}
                  variant={type === option.value ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Stack>
          </Box>

          {/* Client (optional) */}
          {clients.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Link to Client (optional)</InputLabel>
              <Select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                label="Link to Client (optional)"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client._id} value={client._id}>
                    {client.firstName} {client.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          Press Ctrl + Enter to save
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createNote.isPending}
        >
          {createNote.isPending ? 'Saving...' : 'Save Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickCaptureModal;
